import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { PageHeader } from '../../components/PageHeader'
import { ActivityChoices, ExperimentProtocol } from '../../components/capture/ActivityChoices'
import { EXPERIMENT_WORDS } from '../../components/record/phaseWords'
import { Button, Field, Notice, ProblemNotice } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { createPlan, createRun, listActivities, listRuns, type Activity, type ExperimentPlan, type ExperimentPlanCreate } from '../../lib/experiments/api'
import { formatMinutes, localIsoDay } from '../../lib/format'
import { useResource } from '../../lib/useResource'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { NotFound } from '../NotFound'

interface StartOperation {
  signature: string
  body: ExperimentPlanCreate
  planKey: string
  runKey: string
  plan?: ExperimentPlan
}

/** A changed account remounts all reads, form answers and creation keys. */
export function ChooseExperiment() {
  const { account } = useAuth()
  if (account === null) return <p role="status">{EXPERIMENT_WORDS.loading}</p>
  if (account.experiments_enabled_for_me !== true) return <NotFound />
  return <ChooseExperimentBody key={account.user_id} userId={account.user_id} />
}

function ChooseExperimentBody({ userId }: { userId: string }) {
  const navigate = useNavigate()
  const loadLibrary = useCallback((signal: AbortSignal) => listActivities(signal), [])
  const loadRuns = useCallback((signal: AbortSignal) => listRuns(userId, { signal }), [userId])
  const libraryRead = useResource(loadLibrary)
  const runsRead = useResource(loadRuns)
  const [value, setValue] = useState<Activity['key']>('short_walk')
  const [customName, setCustomName] = useState('')
  const [customSettle, setCustomSettle] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [startError, setStartError] = useState<unknown>(null)
  const operation = useRef<StartOperation | null>(null)
  const pending = useRef<AbortController | null>(null)
  const form = useRef<HTMLFormElement | null>(null)
  const [headingRef, armLibraryLanding] = useRetryLanding(libraryRead.error !== null)
  const [countsRef, armCountsLanding] = useRetryLanding(runsRead.error !== null)

  useEffect(() => () => pending.current?.abort(), [])

  const library = libraryRead.data
  const activity = library?.activities.find((item) => item.key === value) ?? library?.activities[0]
  const isCustom = activity?.key === 'custom'
  const settleValue = customSettle ?? String(activity?.settle_s ?? '')

  const start = async (element: HTMLFormElement) => {
    if (pending.current !== null || library === null || activity === undefined || !element.reportValidity()) return
    const body: ExperimentPlanCreate = {
      user_id: userId,
      activity_key: activity.key,
      ...(isCustom ? { custom_name: customName, settle_s: Number(settleValue) } : {}),
    }
    const signature = JSON.stringify(body)
    // A transport retry replays each POST's key. If only run creation failed,
    // the accepted plan is reused rather than creating another plan first.
    const previous = operation.current
    const next: StartOperation = previous?.signature === signature ? previous : {
      signature, body, planKey: crypto.randomUUID(), runKey: crypto.randomUUID(),
    }
    operation.current = next
    const controller = new AbortController()
    pending.current = controller
    setBusy(true)
    setStartError(null)
    try {
      if (next.plan === undefined) next.plan = await createPlan(next.body, next.planKey, controller.signal)
      controller.signal.throwIfAborted()
      if (next.plan.user_id !== userId || next.plan.activity_key !== activity.key) {
        throw new Error('The plan did not match the requested experiment.')
      }
      const run = await createRun(next.plan.id, next.runKey, controller.signal)
      controller.signal.throwIfAborted()
      if (run.user_id !== userId || run.plan_id !== next.plan.id || run.activity_key !== activity.key) {
        throw new Error('The run did not match the requested experiment.')
      }
      void navigate(`/record/experiment/${encodeURIComponent(run.id)}`)
    } catch (error) {
      if (!controller.signal.aborted) setStartError(error)
    } finally {
      if (!controller.signal.aborted) {
        pending.current = null
        setBusy(false)
      }
    }
  }

  const header = <PageHeader
    title={EXPERIMENT_WORDS.title}
    eyebrow={EXPERIMENT_WORDS.eyebrow}
    lead={EXPERIMENT_WORDS.lead}
    back={<Link className="page__back" to="/record/connect">← Record</Link>}
    ref={headingRef}
    tabIndex={-1}
  />

  if (libraryRead.isLoading || library === null) return <div className="stack stack--wide experiment-flow">
    {header}
    {libraryRead.error !== null
      ? <ProblemNotice error={libraryRead.error} onRetry={libraryRead.reload} retryBusy={libraryRead.isLoading} landing={armLibraryLanding}>{EXPERIMENT_WORDS.libraryError}</ProblemNotice>
      : <p role="status">{EXPERIMENT_WORDS.loading}</p>}
  </div>

  if (activity === undefined) return <div className="stack stack--wide experiment-flow">
    {header}
    <Notice><p>{EXPERIMENT_WORDS.libraryEmpty}</p><Button variant="secondary" onClick={libraryRead.reload}>Try again</Button></Notice>
  </div>

  const control = library.activities.find((item) => item.key === 'quiet_rest' && item.control)
  const selectedRuns = runsRead.data?.filter((run) => run.activity_key === activity.key)
  const controlDays = new Set(runsRead.data?.filter((run) => run.activity_key === control?.key)
    .map((run) => localIsoDay(new Date(run.created_at))))
  const previewPlan = {
    reading_s: library.reading_s,
    settle_s: isCustom && settleValue !== '' ? Number(settleValue) : activity.settle_s,
    custom_name: isCustom && customName !== '' ? customName : null,
  }

  return <div className="stack stack--wide experiment-flow">
    {header}
    <form ref={form} className="experiment-flow__form" onSubmit={(event) => { event.preventDefault(); void start(event.currentTarget) }}>
      <fieldset disabled={busy} className="experiment-flow__fields">
        <legend className="visually-hidden">{EXPERIMENT_WORDS.oneActivity}</legend>
        <ActivityChoices library={library} value={activity.key} onChange={setValue} />
        {isCustom && <div className="experiment-flow__custom-fields">
          <Field label={EXPERIMENT_WORDS.customName} value={customName} onChange={(event) => setCustomName(event.currentTarget.value)} required
            minLength={library.custom.name_min_chars} maxLength={library.custom.name_max_chars} />
          <Field label={EXPERIMENT_WORDS.customSettle} type="number" inputMode="numeric" value={settleValue}
            onChange={(event) => setCustomSettle(event.currentTarget.value)} required
            min={library.custom.settle_min_s} max={library.custom.settle_max_s}
            hint={`${formatMinutes(library.custom.settle_min_s)}–${formatMinutes(library.custom.settle_max_s)}`} />
        </div>}
      </fieldset>
      <ExperimentProtocol plan={previewPlan} activity={activity} />
      {startError !== null && <ProblemNotice error={startError} onRetry={() => form.current?.requestSubmit()} retryBusy={busy}>{EXPERIMENT_WORDS.startError}</ProblemNotice>}
      <div className="experiment-flow__start">
        <Button type="submit" busy={busy}>{EXPERIMENT_WORDS.start}</Button>
        <div className="experiment-flow__counts" ref={countsRef} tabIndex={-1}>
          {runsRead.error !== null
            ? <ProblemNotice error={runsRead.error} onRetry={runsRead.reload} retryBusy={runsRead.isLoading} landing={armCountsLanding}>{EXPERIMENT_WORDS.countsError}</ProblemNotice>
            : runsRead.isLoading || selectedRuns === undefined
              ? <span role="status">{EXPERIMENT_WORDS.countsLoading}</span>
              : <span>{activity.name} {EXPERIMENT_WORDS.runsSoFar}: {selectedRuns.length}{control && <> · {control.name} {EXPERIMENT_WORDS.controlDays}: {controlDays.size}</>}</span>}
        </div>
      </div>
    </form>
  </div>
}
